import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockPipelineService } from '@/services/mock/MockPipelineService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { PipelineRecord } from '@/types/domain/pipeline';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { SourceBadge } from '@/components/data-display/SourceBadge';
import { formatCurrency } from '@/lib/utils';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import {
  ArrowLeft,
  Clock,
  FileText,
  MessageSquare,
  AlertTriangle,
  History,
  Layers,
  RefreshCw,
} from 'lucide-react';
import { ProcurementPipeline, PipelineStage } from '@/components/data-display/ProcurementPipeline';
import { PipelineThreeWayMatchCard } from '@/components/pipeline/PipelineThreeWayMatchCard';
import { PipelineTimelineStream } from '@/components/pipeline/PipelineTimelineStream';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';

export const PipelineDetailPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<PipelineRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'STAGES' | 'TIMELINE' | 'DOCUMENTS' | 'COMMENTS'>('OVERVIEW');

  const loadPipelineDetail = async () => {
    if (!requestId) return;
    try {
      setIsLoading(true);
      const detail = await mockPipelineService.getPipelineRecordById(requestId);
      if (!detail) {
        navigate(ROUTES.PIPELINE.LIST);
        return;
      }
      setData(detail);

      const [docs, comms] = await Promise.all([
        mockDocumentCommentService.getDocuments('REQUEST', detail.requestNumber),
        mockDocumentCommentService.getComments('REQUEST', detail.requestNumber),
      ]);
      setDocuments(docs);
      setComments(comms);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadPipelineDetail();
  }, [requestId]);

  const handleRetrySap = async () => {
    if (!data) return;
    try {
      await mockPipelineService.retrySapSync(data.id);
      await loadPipelineDetail();
    } catch {
      alert('Failed to retry SAP sync.');
    }
  };

  if (isLoading || !data) {
    return (
      <div className="p-12 text-center text-xs text-foreground-secondary font-medium font-heading">
        Loading ProcureAI Lifecycle Command Center...
      </div>
    );
  }

  // Convert pipeline stages to ProcurementPipeline component format
  const pipelineStages: PipelineStage[] = data.stages.map((s) => ({
    key: s.key,
    label: s.label,
    status:
      s.status === 'COMPLETED'
        ? 'completed'
        : s.status === 'IN_PROGRESS' || s.key === data.currentStage
        ? 'active'
        : s.status === 'ERROR'
        ? 'error'
        : s.status === 'BLOCKED'
        ? 'blocked'
        : 'pending',
    documentNumber: s.documentNumber,
  }));

  return (
    <div ref={pageRef} className="space-y-6 pb-24 font-heading">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.PIPELINE.LIST)}
            className="h-9 rounded-xl border-border text-foreground hover:bg-muted font-bold"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Pipeline
          </Button>

          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-extrabold text-foreground">{data.requestNumber}</span>
            <SourceBadge source={data.source} showIcon={false} className="text-[10px]" />
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          {data.isDelayed && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
              <Clock className="h-3.5 w-3.5" /> Delivery Delayed ({data.delayDays}d)
            </span>
          )}

          {data.isBlocked && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-extrabold bg-rose-500/10 text-rose-700 dark:text-rose-300 border border-rose-500/20 animate-pulse">
              <AlertTriangle className="h-3.5 w-3.5" /> Stage Blocked
            </span>
          )}

          <StatusBadge status={data.currentStatus} category="PurchaseRequest" />
        </div>
      </div>

      {/* Main Request Metadata Card */}
      <Card className="border-border rounded-2xl shadow-2xs">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <span className="text-xs font-extrabold text-primary uppercase tracking-wider">
                {data.category || 'CapitalEquipment'} • {data.capexOpex || 'OPEX'}
              </span>
              <h1 className="font-heading text-2xl font-extrabold text-foreground">{data.title || 'Procurement Requisition'}</h1>
              <p className="text-xs text-foreground-secondary font-medium">
                Cost Center: <strong className="text-foreground font-mono">{data.costCenter || 'CC-IT-101'}</strong> • Created {new Date(data.createdDate || Date.now()).toLocaleDateString()}
              </p>
            </div>

            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 text-right space-y-1 min-w-[200px]">
              <span className="text-[11px] font-bold text-foreground-muted uppercase">Total Procurement Value</span>
              <span className="font-heading text-2xl font-extrabold text-primary block">
                {formatCurrency(data.totalAmount, data.currency)}
              </span>
            </div>
          </div>

          {/* Key Connected Identifiers Bar */}
          <div className="flex flex-wrap items-center gap-2 text-xs pt-1">
            <span className="font-bold text-foreground-muted uppercase text-[10px] mr-1">Connected Identifiers:</span>
            {data.connectedIds.nfaId && (
              <span className="bg-muted px-2 py-1 rounded font-mono font-bold text-foreground">
                NFA: {data.connectedIds.nfaId}
              </span>
            )}
            {data.connectedIds.sapPrNumber && (
              <span className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/20 px-2 py-1 rounded font-mono font-bold">
                SAP PR: {data.connectedIds.sapPrNumber}
              </span>
            )}
            {data.connectedIds.rfqId && (
              <span className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20 px-2 py-1 rounded font-mono font-bold">
                RFQ: {data.connectedIds.rfqId}
              </span>
            )}
            {data.connectedIds.poNumber && (
              <button
                onClick={() => navigate(`/purchase-orders/${data.connectedIds.poNumber}`)}
                className="bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20 hover:bg-purple-500/20 px-2 py-1 rounded font-mono font-bold transition-colors cursor-pointer"
              >
                PO: {data.connectedIds.poNumber}
              </button>
            )}
            {data.connectedIds.grnNumber && (
              <button
                onClick={() => navigate(ROUTES.GRN.DETAILS(data.connectedIds.grnNumber!))}
                className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20 hover:bg-emerald-500/20 px-2 py-1 rounded font-mono font-bold transition-colors cursor-pointer"
              >
                GRN: {data.connectedIds.grnNumber}
              </button>
            )}
            {data.connectedIds.invoiceNumber && (
              <button
                onClick={() => navigate(ROUTES.INVOICES.DETAILS(data.connectedIds.invoiceNumber!))}
                className="bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20 hover:bg-purple-500/20 px-2 py-1 rounded font-mono font-bold transition-colors cursor-pointer"
              >
                INV: {data.connectedIds.invoiceNumber}
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Blocked Exception Banner */}
      {data.isBlocked && (
        <div className="flex items-center justify-between rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-xs font-heading">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" />
            <div>
              <p className="font-extrabold text-rose-700 dark:text-rose-300">Procurement Blocked: {data.blockedReason}</p>
              <p className="text-[11px] text-rose-600 dark:text-rose-400">
                Action required on stage {data.currentStage.replace('_', ' ')} before proceeding to downstream workflow.
              </p>
            </div>
          </div>

          {data.currentStage === 'SAP_PR' && (
            <Button size="sm" onClick={handleRetrySap} className="rounded-xl font-bold bg-rose-600 hover:bg-rose-700 text-white">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry SAP Integration
            </Button>
          )}
        </div>
      )}

      {/* Visual 12-Stage Progress Component */}
      <ProcurementPipeline
        currentStage={data.currentStage}
        stages={pipelineStages}
      />

      {/* Tabs Navigation Bar */}
      <div className="flex items-center gap-2 border-b border-border pb-1">
        <button
          onClick={() => setActiveTab('OVERVIEW')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'OVERVIEW'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <FileText className="h-4 w-4" /> Overview & 3-Way Match
        </button>

        <button
          onClick={() => setActiveTab('STAGES')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'STAGES'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <Layers className="h-4 w-4" /> All 12 Stage Details
        </button>

        <button
          onClick={() => setActiveTab('TIMELINE')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'TIMELINE'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <History className="h-4 w-4" /> Multi-System Audit Stream ({data.timeline.length})
        </button>

        <button
          onClick={() => setActiveTab('DOCUMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'DOCUMENTS'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <FileText className="h-4 w-4" /> Documents & Files ({documents.length})
        </button>

        <button
          onClick={() => setActiveTab('COMMENTS')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-extrabold transition-all ${
            activeTab === 'COMMENTS'
              ? 'bg-primary text-primary-foreground shadow-2xs'
              : 'text-foreground-secondary hover:bg-muted'
          }`}
        >
          <MessageSquare className="h-4 w-4" /> Discussions ({comments.length})
        </button>
      </div>

      {/* TAB 1: OVERVIEW & 3-WAY MATCH */}
      {activeTab === 'OVERVIEW' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <PipelineThreeWayMatchCard
              threeWayMatch={data.threeWayMatch}
              currency={data.currency}
            />

            {/* AI Insights Card */}
            {data.aiInsight && (
              <Card className="border-copper/30 bg-copper/5 rounded-2xl shadow-2xs">
                <CardHeader className="pb-3 border-b border-copper/20">
                  <CardTitle className="font-heading text-sm font-bold text-copper-dark dark:text-amber-300">
                    {data.aiInsight.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-3.5 text-xs text-foreground-secondary leading-relaxed space-y-2">
                  <p>{data.aiInsight.summary}</p>
                  <p className="font-bold text-emerald-600 dark:text-emerald-400">
                    Realized Savings: {data.aiInsight.potentialSavingsFormatted}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card className="border-border rounded-2xl shadow-2xs">
              <CardHeader className="pb-3 border-b border-border/60">
                <CardTitle className="font-heading text-xs font-bold text-foreground uppercase">
                  Requester & Location Metadata
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3.5 space-y-2 text-xs">
                <p>Requester: <strong className="text-foreground">{data.requesterName}</strong></p>
                <p>Department: <strong className="text-foreground">{data.departmentName}</strong></p>
                <p>Plant: <strong className="text-foreground">{data.plantName}</strong></p>
                <p>Company Code: <span className="font-mono text-primary font-bold">{data.companyCode}</span></p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* TAB 2: ALL 12 STAGE DETAILS */}
      {activeTab === 'STAGES' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.stages.map((stg, idx) => (
            <Card key={stg.key} className="border-border rounded-2xl shadow-2xs p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-foreground">{idx + 1}. {stg.label}</span>
                <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded bg-muted text-foreground-secondary">
                  {stg.status}
                </span>
              </div>
              {stg.documentNumber && (
                <p className="font-mono text-xs font-bold text-primary">Doc Ref: {stg.documentNumber}</p>
              )}
              {stg.completedDate && (
                <p className="text-[11px] text-foreground-muted font-mono">Completed: {stg.completedDate}</p>
              )}
              {stg.exceptionMsg && (
                <p className="text-[11px] font-bold text-rose-500">{stg.exceptionMsg}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* TAB 3: AUDIT STREAM */}
      {activeTab === 'TIMELINE' && (
        <div className="max-w-3xl">
          <PipelineTimelineStream events={data.timeline} />
        </div>
      )}

      {/* TAB 4: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <div className="space-y-6">
          <DocumentManagementPanel
            documents={documents}
            onUploadFile={async (file, cat) => {
              await mockDocumentCommentService.uploadDocument('REQUEST', data.requestNumber, file, cat);
              loadPipelineDetail();
            }}
            onDownloadFile={async (docId) => {
              const res = await mockDocumentCommentService.downloadDocument(docId);
              window.open(res.downloadUrl, '_blank');
            }}
            onDeleteFile={async (docId) => {
              await mockDocumentCommentService.deleteDocument(docId);
              loadPipelineDetail();
            }}
          />
        </div>
      )}

      {/* TAB 5: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <div className="max-w-3xl">
          <EnterpriseCommentThread
            comments={comments}
            attachments={documents}
            onAddComment={async (text, attIds) => {
              await mockDocumentCommentService.createComment('REQUEST', data.requestNumber, text, attIds);
              loadPipelineDetail();
            }}
            onUpdateComment={async (commentId, text) => {
              await mockDocumentCommentService.updateComment(commentId, text);
              loadPipelineDetail();
            }}
            onDeleteComment={async (commentId) => {
              await mockDocumentCommentService.deleteComment(commentId);
              loadPipelineDetail();
            }}
          />
        </div>
      )}
    </div>
  );
};
